import React, { useState, useEffect } from 'react';
import {
  ITEM_SNOOZE_PRESETS,
  DEFAULT_ITEM_SNOOZE_PRESET,
  DEFAULT_ITEM_SNOOZE_PRESET_KEY,
  MAX_SNOOZE_HELPER_TEXT,
  maxSnoozeDate,
} from './itemSnoozePresets';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
  Stack,
  Autocomplete,
  createFilterOptions
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatSnoozeDateTime, PICKER_DATE_TIME_FORMAT } from '../../utils/formatTime';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  getStoreItemAvailability,
  createItemBlackout,
  deleteItemBlackout,
  bulkDeleteItemBlackouts
} from '../../api';
import { useProductVariantScope } from '../../hooks/useProductVariantScope';
import { useSnoozeItemOptions } from '../../hooks/useSnoozeItemOptions';
import { VariantScopeList } from './VariantScopeList';
import {
  BlackoutItem,
  BlackoutFormData,
  BlackoutType,
  BlackoutTypeLabels,
  BlackoutReasonLabels,
  BlackoutTypeHelp,
  SELECTABLE_BLACKOUT_TYPES,
  getBlackoutType,
  getItemCode,
  getItemDisplayName
} from './types';
import { HelpTooltip } from '../Onboarding/HelpTooltip';

interface ItemAvailabilityProps {
  storeNumber: string;
}

const ItemAvailability: React.FC<ItemAvailabilityProps> = ({ storeNumber }) => {
  const { use24HourFormat } = useTimeFormat();
  const [blackouts, setBlackouts] = useState<BlackoutItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedBlackout, setSelectedBlackout] = useState<BlackoutItem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Multi-select activate. Rows keyed `${type}:${code}` (unique per blackout).
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [bulkActivateConfirmOpen, setBulkActivateConfirmOpen] = useState(false);
  const rowKey = (b: BlackoutItem) => `${getBlackoutType(b)}:${getItemCode(b)}`;

  // Default expiry to the next 5 AM (aligned with channel snooze end-of-day)
  const getEndOfDay = () => DEFAULT_ITEM_SNOOZE_PRESET.getValue();

  const [formData, setFormData] = useState<BlackoutFormData>({
    type: 'PRODUCT',
    itemCode: '',
    reason: 'OUT_OF_STOCK',
    expiry: getEndOfDay(),
    startingAt: new Date()
  });

  // Track which quick option is selected for highlighting
  const [selectedQuickOption, setSelectedQuickOption] =
    useState<string>(DEFAULT_ITEM_SNOOZE_PRESET_KEY);

  // Item options: the store's live published menus when loaded (browsable
  // list, snoozed items excluded), catalog search as loading/error fallback
  // and behind the "Search full catalog" toggle.

  // Multiple items selection
  const [selectedItems, setSelectedItems] = useState<Array<{
    type: BlackoutType;
    code: string;
    name: string;
  }>>([]);

  // Variant drill-down — one scope choice per selected product, so a
  // multi-item selection can mix whole-product and specific-variant snoozes.
  const variantScope = useProductVariantScope({
    isProductType: formData.type === 'PRODUCT',
    selectedItems,
  });
  const { resetVariantState, hasInvalidVariantScope, buildSubmitPayload, getSnoozeUnitCount } =
    variantScope;
  // How many blackouts the submit will actually create — a product narrowed to
  // 2 of its 4 variants counts as 2, not 1.
  const snoozeUnitCount = getSnoozeUnitCount(selectedItems);
  const snoozeButtonLabel =
    snoozeUnitCount > 0
      ? `Snooze ${snoozeUnitCount} item${snoozeUnitCount === 1 ? '' : 's'}`
      : 'Snooze Item';

  const {
    mode: optionsMode,
    options: itemOptions,
    visibleMenuOptions,
    inputValue: searchQuery,
    onInputChange: setSearchQuery,
    isLoading: isSearching,
    menuStatus,
    useCatalogSearch,
    setUseCatalogSearch,
    partial: menuPartial,
    reset: resetItemOptions,
  } = useSnoozeItemOptions({
    storeNumber,
    itemType: formData.type,
    blackouts,
  });

  const handleSelectAllShown = () => {
    setSelectedItems(prev => {
      const byCode = new Map(prev.map(i => [i.code, i]));
      for (const opt of visibleMenuOptions) {
        if (!byCode.has(opt.code)) {
          byCode.set(opt.code, { type: formData.type, code: opt.code, name: opt.name });
        }
      }
      return [...byCode.values()];
    });
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (createDialogOpen) {
      setFormData({
        type: 'PRODUCT',
        itemCode: '',
        reason: 'OUT_OF_STOCK',
        expiry: getEndOfDay(),
        startingAt: new Date()
      });
      resetItemOptions();
      setSelectedItems([]);
      setSelectedQuickOption(DEFAULT_ITEM_SNOOZE_PRESET_KEY);
      resetVariantState();
    }
  }, [createDialogOpen]);

  const fetchBlackouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStoreItemAvailability(storeNumber, 100);
      setBlackouts(response?.blackouts || []);
    } catch (err) {
      console.error('Failed to fetch snoozed items:', err);
      setError('Failed to load snoozed items');
      setBlackouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlackouts();
  }, [storeNumber]);

  const handleCreateBlackout = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item');
      return;
    }

    if (!formData.expiry) {
      setError('Please set an expiry date');
      return;
    }

    if (hasInvalidVariantScope) {
      setError('Pick at least one variant to snooze, or switch that product to "Whole product"');
      return;
    }

    if (formData.expiry > maxSnoozeDate()) {
      setError(MAX_SNOOZE_HELPER_TEXT);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Splits the selection by type and applies each product's variant scope.
      // Products narrowed to specific variants contribute only variant codes —
      // the parent is left available so sibling variants stay orderable.
      // `auditItems` carries display names; without it the Activity Log falls
      // back to bare item codes.
      const {
        productCodes,
        bundleCodes,
        variantCodes,
        modifierCodes,
        optionValueCodes,
        auditItems,
      } = buildSubmitPayload(selectedItems);

      await createItemBlackout(
        [storeNumber],
        productCodes,
        bundleCodes,
        formData.reason,
        formData.expiry.toISOString(),
        formData.startingAt?.toISOString(),
        variantCodes,
        modifierCodes,
        optionValueCodes,
        auditItems
      );
      setCreateDialogOpen(false);
      setFormData({
        type: 'PRODUCT',
        itemCode: '',
        reason: 'OUT_OF_STOCK',
        expiry: getEndOfDay(),
        startingAt: new Date()
      });
      fetchBlackouts();
    } catch (err) {
      console.error('Failed to snooze item:', err);
      setError('Failed to snooze item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlackout = async () => {
    if (!selectedBlackout) return;

    setSubmitting(true);
    setError(null);

    try {
      const blackoutType = getBlackoutType(selectedBlackout);
      const itemCode = getItemCode(selectedBlackout);

      await deleteItemBlackout(
        storeNumber,
        blackoutType,
        itemCode,
        selectedBlackout.name
      );

      setDeleteConfirmOpen(false);
      setSelectedBlackout(null);

      // Show success message
      setError(null); // Clear any existing errors
      setSuccess(`Successfully activated ${itemCode}`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

      // Refresh the list
      await fetchBlackouts();
    } catch (err: any) {
      console.error('Failed to activate item:', err);
      const errorMessage = err.response?.data?.message ||
                          err.message ||
                          'Failed to activate item';
      setError(errorMessage);
      // Keep dialog open on error so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  // Clear selection whenever the list reloads so stale keys can't linger.
  useEffect(() => {
    setSelectedRows(new Set());
  }, [blackouts]);

  const toggleRow = (blackout: BlackoutItem) => {
    const key = rowKey(blackout);
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRows(prev =>
      prev.size === blackouts.length ? new Set() : new Set(blackouts.map(rowKey))
    );
  };

  const handleBulkActivate = async () => {
    const selected = blackouts.filter(b => selectedRows.has(rowKey(b)));
    if (selected.length === 0) return;

    setBulkActivateConfirmOpen(false);
    setSubmitting(true);
    setError(null);

    try {
      // Group selected codes by type for the existing bulk-delete endpoint,
      // and carry names for the Activity Log.
      const codesByType: Record<string, string[]> = {
        PRODUCT: [], BUNDLE: [], VARIANT: [], MODIFIER: [], OPTION_VALUE: [],
      };
      const items = selected.map(b => {
        const type = getBlackoutType(b);
        const code = getItemCode(b);
        codesByType[type].push(code);
        return { type, code, name: b.name || code };
      });

      const response = await bulkDeleteItemBlackouts({
        storeNumbers: [storeNumber],
        productCodes: codesByType.PRODUCT,
        bundleCodes: codesByType.BUNDLE,
        variantCodes: codesByType.VARIANT,
        modifierCodes: codesByType.MODIFIER,
        optionValueCodes: codesByType.OPTION_VALUE,
        items,
      });

      const failed = response?.summary?.failed ?? 0;
      if (response?.status && failed === 0) {
        setSuccess(`Activated ${selected.length} item${selected.length === 1 ? '' : 's'}`);
        setTimeout(() => setSuccess(null), 3000);
        setSelectedRows(new Set());
        await fetchBlackouts();
      } else {
        setError(`Activated ${response?.summary?.succeeded ?? 0}, failed ${failed}. Please retry.`);
        await fetchBlackouts();
      }
    } catch (err: any) {
      console.error('Failed to bulk activate items:', err);
      setError(err.response?.data?.message || err.message || 'Failed to activate items');
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = (expiry: string) => {
    return new Date(expiry) < new Date();
  };

  const getTimeRemaining = (expiry: string) => {
    const now = new Date();
    const expiryDate = new Date(expiry);
    const diff = expiryDate.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    }

    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <Card>
      <CardHeader
        title="Snooze Items"
        subheader="Mark menu items as temporarily unavailable (snoozed)"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchBlackouts} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading}
            >
              Snooze an Item
            </Button>
          </Box>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : blackouts.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No items are currently snoozed
          </Typography>
        ) : (
          <>
            {selectedRows.size > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {selectedRows.size} selected
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={submitting}
                  onClick={() => setBulkActivateConfirmOpen(true)}
                >
                  Activate selected ({selectedRows.size})
                </Button>
                <Button size="small" onClick={() => setSelectedRows(new Set())} disabled={submitting}>
                  Clear
                </Button>
              </Box>
            )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.size > 0 && selectedRows.size < blackouts.length}
                      checked={blackouts.length > 0 && selectedRows.size === blackouts.length}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blackouts.map((blackout, index) => {
                  const type = getBlackoutType(blackout);
                  const expired = isExpired(blackout.expiry);
                  const selected = selectedRows.has(rowKey(blackout));

                  return (
                    <TableRow key={index} selected={selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleRow(blackout)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={BlackoutTypeLabels[type]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getItemDisplayName(blackout)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={BlackoutReasonLabels[blackout.reason]}
                          size="small"
                          color={blackout.reason === 'OUT_OF_STOCK' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {formatSnoozeDateTime(new Date(blackout.startingAt), use24HourFormat)}
                      </TableCell>
                      <TableCell>
                        {formatSnoozeDateTime(new Date(blackout.expiry), use24HourFormat)}
                      </TableCell>
                      <TableCell>
                        {expired ? (
                          <Chip label="Expired" size="small" color="default" />
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {getTimeRemaining(blackout.expiry)}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            sx={{ color: 'success.main' }}
                            onClick={() => {
                              setSelectedBlackout(blackout);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </CardContent>

      {/* Create Snooze Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          // Reset form and search state
          setFormData({
            type: 'PRODUCT',
            itemCode: '',
            reason: 'OUT_OF_STOCK',
            expiry: null,
            startingAt: new Date()
          });
          resetItemOptions();
          setSelectedItems([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Snooze Menu Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Type"
                select
                value={formData.type}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, type: e.target.value as BlackoutType }));
                  resetItemOptions();
                  setSelectedItems([]);
                  resetVariantState();
                }}
                fullWidth
                helperText={BlackoutTypeHelp[formData.type]}
              >
                {SELECTABLE_BLACKOUT_TYPES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {BlackoutTypeLabels[value]}
                  </MenuItem>
                ))}
              </TextField>
              <HelpTooltip title={BlackoutTypeHelp[formData.type]} />
            </Stack>

            {/* Select-all affordance for the live-menu list. Acts on the
                currently-shown (text-filtered) options so users can snooze a
                whole category in one tap instead of clicking each. */}
            {optionsMode === 'menu' && visibleMenuOptions.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button size="small" onClick={handleSelectAllShown}>
                  {searchQuery.trim()
                    ? `Select all shown (${visibleMenuOptions.length})`
                    : `Select all (${visibleMenuOptions.length})`}
                </Button>
                {selectedItems.length > 0 && (
                  <Button size="small" color="inherit" onClick={() => setSelectedItems([])}>
                    Clear ({selectedItems.length})
                  </Button>
                )}
              </Box>
            )}

            {/* Autocomplete for all types - Multi-select */}
            <Autocomplete
              multiple
              value={selectedItems.map(item => ({
                code: item.code,
                name: item.name,
                label: `${item.code} - ${item.name}`
              }))}
              onChange={(_, newValue) => {
                const updatedItems = newValue.map(item => ({
                  type: formData.type,
                  code: item.code,
                  name: item.name
                }));
                setSelectedItems(updatedItems);
              }}
              inputValue={searchQuery}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setSearchQuery(newInputValue);
                }
              }}
              options={itemOptions}
              // Menu mode: full live-menu list filtered locally on code+name
              // (browsable on focus). Search mode: server already filtered.
              filterOptions={
                optionsMode === 'menu'
                  ? createFilterOptions({ stringify: (option) => `${option.code} ${option.name}` })
                  : (x) => x
              }
              getOptionLabel={(option) => option.label || `${option.code} - ${option.name}`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              disableCloseOnSelect
              loading={isSearching}
              loadingText="Searching..."
              noOptionsText={
                optionsMode === 'menu'
                  ? `No matching ${BlackoutTypeLabels[formData.type].toLowerCase()}s on this store's live menu`
                  : searchQuery.length < 1
                  ? `Start typing an item name to search ${BlackoutTypeLabels[formData.type].toLowerCase()}s`
                  : 'No results found'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item Name"
                  placeholder={
                    selectedItems.length === 0
                      ? optionsMode === 'menu'
                        ? `Browse or filter ${BlackoutTypeLabels[formData.type].toLowerCase()}s on the live menu…`
                        : `Search ${BlackoutTypeLabels[formData.type].toLowerCase()}s by name…`
                      : ''
                  }
                  required
                  helperText={
                    optionsMode === 'menu'
                      ? `Showing ${itemOptions.length} ${BlackoutTypeLabels[formData.type].toLowerCase()}s on this store's live menu${menuPartial ? ' (some menus unavailable)' : ''}.`
                      : menuStatus === 'loading'
                      ? "Loading this store's live menu… searching the full catalog meanwhile."
                      : 'Results include all menu items; confirm the item is on your current menu before snoozing.'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.code}
                    label={
                      <Box component="span">
                        <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          {option.code}
                        </Typography>
                      </Box>
                    }
                    size="small"
                  />
                ))
              }
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props as any;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.code}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
            />

            {/* Live-menu source controls */}
            {menuStatus === 'empty' && !useCatalogSearch && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                No live published menu items found for this store — searching the full catalog.
              </Alert>
            )}
            {(menuStatus === 'ready' || menuStatus === 'partial') && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setUseCatalogSearch(!useCatalogSearch);
                    resetItemOptions();
                  }}
                >
                  {useCatalogSearch ? 'Back to live menu items' : 'Search full catalog'}
                </Button>
              </Box>
            )}

            {/* Per-product variant drill-down. Each selected product keeps its
                own scope, so adding a second item no longer discards the
                first item’s variant picks. */}
            <VariantScopeList scope={variantScope} presentation="desktop" />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                format={PICKER_DATE_TIME_FORMAT}
                label="Starts At"
                value={formData.startingAt}
                onChange={(newValue) => setFormData(prev => ({ ...prev, startingAt: newValue || new Date() }))}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />

              <DateTimePicker
                format={PICKER_DATE_TIME_FORMAT}
                label="Expires At"
                value={formData.expiry}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, expiry: newValue }));
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: formData.expiry
                      ? `Reactivates: ${formatSnoozeDateTime(formData.expiry, use24HourFormat)}. ${MAX_SNOOZE_HELPER_TEXT}`
                      : MAX_SNOOZE_HELPER_TEXT
                  }
                }}
                minDateTime={formData.startingAt || new Date()}
                // Byte Graph rejects expiries past the ceiling, so grey them out
                // rather than letting the user pick one and fail on submit.
                maxDateTime={maxSnoozeDate()}
              />
            </LocalizationProvider>

            {/* Quick Expiry Options — shared with mobile and the bulk editor */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick Options
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {ITEM_SNOOZE_PRESETS.map((preset) => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, expiry: preset.getValue() }));
                      setSelectedQuickOption(preset.key);
                    }}
                    variant={selectedQuickOption === preset.key ? 'filled' : 'outlined'}
                    color={selectedQuickOption === preset.key ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            // Reset form and search state
            setFormData({
              type: 'PRODUCT',
              itemCode: '',
              reason: 'OUT_OF_STOCK',
              expiry: getEndOfDay(),
              startingAt: new Date()
            });
            resetItemOptions();
            setSelectedItems([]);
          }} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateBlackout}
            variant="contained"
            disabled={submitting || selectedItems.length === 0 || !formData.expiry || hasInvalidVariantScope}
          >
            {submitting ? <CircularProgress size={20} /> : snoozeButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Activate Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to activate this item? It will become available immediately.
          </Typography>
          {selectedBlackout && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Type: {BlackoutTypeLabels[getBlackoutType(selectedBlackout)]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Item Code: {getItemCode(selectedBlackout)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reason: {BlackoutReasonLabels[selectedBlackout.reason]}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBlackout}
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Activate Confirmation Dialog */}
      <Dialog open={bulkActivateConfirmOpen} onClose={() => setBulkActivateConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Activate {selectedRows.size} item{selectedRows.size === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            These items will be un-snoozed and immediately available again:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0, maxHeight: 200, overflow: 'auto' }}>
            {blackouts
              .filter(b => selectedRows.has(rowKey(b)))
              .map(b => (
                <li key={rowKey(b)}>
                  <Typography variant="body2">{getItemDisplayName(b)}</Typography>
                </li>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActivateConfirmOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleBulkActivate} variant="contained" color="success" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : `Activate ${selectedRows.size}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ItemAvailability;